import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetUsersByFilter_usersByFilter_edges_node as User,
    GetUsersByFilter as GetUsersByFilterQuery,
    GetUsersByFilterVariables as GetUsersByFilterQueryVariables,
} from 'nutshell-graphql-types';

import GET_USERS_BY_FILTER from '../queries/get-users-by-filter.graphql';

const LIMIT = 50;

export const useGetUsersByFilter = (input: {
    statusFilter?: number;
    roleId?: string;
}): {
    isLoading: boolean;
    errorMessage: string | null | undefined;
    users: User[];
    hasNextPage: boolean;
    totalCount: number;
    endCursor: string | null | undefined;
    fetchMoreRows: () => void;
    refetch: () => Promise<any>;
} => {
    const {loading, data, error, fetchMore, refetch} = useQuery<
        GetUsersByFilterQuery,
        GetUsersByFilterQueryVariables
    >(GET_USERS_BY_FILTER, {
        variables: {first: LIMIT, ...input},
        notifyOnNetworkStatusChange: true,
    });

    const usersByFilter = data ? data.usersByFilter : null;
    const totalCount = usersByFilter ? usersByFilter.totalCount : 0;
    const pageInfo = usersByFilter ? usersByFilter.pageInfo : null;
    const endCursor = pageInfo ? pageInfo.endCursor : null;
    const hasNextPage = pageInfo ? pageInfo.hasNextPage : false;

    const fetchMoreRows = () => {
        return fetchMore({
            query: GET_USERS_BY_FILTER,
            variables: {
                ...input,
                first: LIMIT,
                after: endCursor,
            },
            // @ts-expect-error
            updateQuery: (previousResult, {fetchMoreResult}) => {
                if (
                    !fetchMoreResult ||
                    // @ts-expect-error
                    !fetchMoreResult.usersByFilter.edges.length > 0 ||
                    !previousResult
                ) {
                    return;
                }

                return {
                    usersByFilter: {
                        __typename: 'UsersConnection',
                        pageInfo: fetchMoreResult.usersByFilter.pageInfo,
                        edges: [
                            ...previousResult.usersByFilter.edges,
                            ...fetchMoreResult.usersByFilter.edges,
                        ],
                    },
                };
            },
        });
    };

    return {
        isLoading: loading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
        users: usersByFilter ? usersByFilter.edges.map((edge) => edge.node) : [],
        hasNextPage,
        totalCount,
        endCursor,
        fetchMoreRows,
        refetch,
    };
};
